/**
 * Shared category mapping — maps section ID prefixes to catalog categories.
 * Used by both the build-time template section extractor and the runtime section matcher.
 */

import { isSharedRole } from './shared-section-types.js';
import { titleCaseCategory } from './section-categories.js';

export const idToCategoryMap = {
  // Hero / interior page headers
  hero: 'hero',
  'int-hero': 'interior-pages',
  banner: 'interior-pages',
  landing: 'interior-pages',
  'content-page': 'interior-pages',
  interior: 'interior-pages',
  about: 'interior-pages',

  // Body content
  services: 'services',
  sbs: 'side-by-side',
  RPsbs: 'side-by-side',
  RPsbsr: 'side-by-side',
  'why-choose-us': 'why-choose-us',
  'why-choose': 'why-choose-us',
  why: 'why-choose-us',
  reviews: 'reviews',
  gallery: 'gallery',
  videos: 'gallery',
  blog: 'blog',
  'single-article': 'blog',
  'article-': 'blog',
  faq: 'faq',
  contact: 'forms-contact',
  'cs-contact': 'forms-contact',
  form: 'forms-contact',
  cta: 'cta',
  stats: 'stats',
  pricing: 'pricing',
  step: 'steps',
  'meet-team': 'meet-the-team',
  staff: 'meet-the-team',
  portfolio: 'portfolio',
  quote: 'quotes',
  'event-details': 'events',

  // Widgets / flair
  'sticky-number': 'content-flair',
  'reading-progress': 'content-flair',
  logos: 'misc',
  itemPagination: 'misc',

  // Shared
  'cs-footer': 'footer',
  navigation: 'navigation',
  'cs-navigation': 'navigation',
  'cs-menu': 'navigation',
  header: 'navigation',

  // Shopify-imported templates
  'shopify-section-header': 'navigation',
  'shopify-section-footer': 'footer',
  'shopify-section-template': 'ecommerce',
  'shopify-section-product': 'ecommerce',

  special: 'special-codestitches'
};

// Sort once: longer prefixes win when matching via startsWith so more-specific
// keys (e.g. `int-hero`, `cs-contact`) beat shorter parents (`hero`, `contact`).
const sortedPrefixes = Object.entries(idToCategoryMap).sort(([a], [b]) => b.length - a.length);

export function inferCategory(sectionId, role) {
  let category = idToCategoryMap[sectionId];
  if (!category) {
    for (const [prefix, cat] of sortedPrefixes) {
      if (sectionId.startsWith(prefix)) {
        category = cat;
        break;
      }
    }
  }
  if (!category && isSharedRole(role)) {
    category = role;
  }
  return category || null;
}

/** A human category name for a catalog card, never its implementation id. */
export function categoryDisplayName(sectionId, role, categoryMap = {}, category = null) {
  const key = category || inferCategory(String(sectionId || ''), role);
  return categoryMap?.[key]?.displayName || (key ? titleCaseCategory(key) : 'Section');
}
